import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosRequestTransformer,
} from 'axios';

import { getConfig } from './appConfig';
import { deepTransformNumericKeysToSnakeCase } from './transformers/deepTransformKeys';

const config = getConfig();

export const BaseAxiosConfig: AxiosRequestConfig = {
  baseURL: config.baseApiPath,
  timeout: 0,
  headers: {
    'Content-Type': 'application/json',
    'Key-Inflection': 'camel',
  },
  transformRequest: [
    data => {
      // this should leave FormData and streams alone
      if (data?.constructor === Object) {
        return deepTransformNumericKeysToSnakeCase(data);
      }

      return data;
    },
    ...(axios.defaults.transformRequest as AxiosRequestTransformer[]),
  ],
};

export const axiosInstance = axios.create(BaseAxiosConfig);

axiosInstance.interceptors.request.use(req => {
  // Strip our Auth headers if we're requesting a resource from
  // anywhere other than our servers
  try {
    if (req.baseURL !== config.baseApiPath) {
      delete req.headers?.['Authorization'];
    }

    const url = new URL(req.url || '');
    if (!url.origin.startsWith(config.baseApiPath)) {
      delete req.headers?.['Authorization'];
    }
  } catch {
    // request was not directed to an external server
  }

  return req;
});

axiosInstance.interceptors.response.use(undefined, async error => {
  const response = error.response as AxiosError['response'];
  if (response?.status === 401) {
    // We're not authorized - clear token and send to a login page.
    localStorage.removeItem('userToken');
    window.location.assign(config.unAuthenticatedRootUrl);
  } else {
    const requestConfig = error.config as AxiosError['config'];

    error.responseBody = JSON.stringify(response?.data);
    error.requestBody = requestConfig?.data;
    error.requestPath = requestConfig?.url;
  }

  return Promise.reject(error);
});

/**
 * Update the `Authorization` header for all Axios requests
 * @param jwtToken The JWT token
 */
export const attachAuthHeader = (jwtToken: string | undefined) => {
  const currentHeaders = axiosInstance.defaults.headers;

  if (jwtToken) {
    axiosInstance.defaults.headers = {
      ...currentHeaders,
      // @ts-expect-error Authorization is a valid header, despite what the typings say
      Authorization: `Bearer ${jwtToken}`,
    };
  } else {
    // @ts-expect-error Authorization is a valid header
    delete axiosInstance.defaults.headers['Authorization'];
  }
};

export default axiosInstance;
